import { useState, useCallback } from "react";
import {
  FetchWarehousesRequest,
  WarehouseInfo,
  UpdateWarehouseRequest,
  FetchWarehousesResponse,
  RegisterWarehouseRequest,
} from "../types";
import { warehouseListApi, updateWarehouseApi } from "../api/warehouseListApi";
import { Severity } from "../../../type";

/**
 * 仕入先倉庫の登録・更新・削除のためのカスタムフック
 * 倉庫情報を取得し、更新するためのロジックを持つ。
 */
export default function useWarehouseRegister(
  showAlert: (msg: string, severity?: Severity) => void,
) {
  const [total, setTotal] = useState(0); // 倉庫情報の総数
  const [warehouseList, setWarehouseList] = useState<WarehouseInfo[]>([]); // 現在表示されている倉庫情報のリスト
  const [isLoading, setIsLoading] = useState(false); // 初回ロード時のローディング状態

  /**
   * 倉庫情報を取得する関数
   * @param searchParams 検索条件パラメータ
   */
  const fetchWarehouses = async (searchParams: FetchWarehousesRequest) => {
    setIsLoading(true);
    try {
      // APIを呼び出し、データを取得
      const res = await warehouseListApi({
        ...searchParams,
      });

      // テスト用の設定。遅延を追加して処理を遅らせる
      // await new Promise((resolve) => {
      //   setTimeout(resolve, 2000);
      // }); // 2秒の遅延

      const warehouses = res.depot_infos; // 取得した倉庫情報
      setTotal(res.total); // 総数を設定
      setWarehouseList(structuredClone(warehouses)); // 倉庫情報リストを更新
    } catch (err) {
      console.error(err);
      showAlert("倉庫一覧情報の取得に失敗しました。", "error");
    } finally {
      setIsLoading(false); // ローディング状態を解除
    }
  };

  /**
   * 倉庫情報を登録する関数
   * @param warehouse 登録する倉庫情報
   */
  const registerWarehouse = async (warehouse: RegisterWarehouseRequest) => {
    setIsLoading(true);
    try {
      const newWarehouse = await updateWarehouseApi({ ...warehouse, operation: 0 });
      setWarehouseList((prevList) => [...prevList, newWarehouse]);
      setTotal(prev => prev + 1);
      // console.log("登録された倉庫情報:", newWarehouse); // 新しく登録された倉庫情報をコンソールに出力
    } catch (err) {
      console.error(err);
      showAlert("倉庫の登録に失敗しました。", "error");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 倉庫情報を更新する関数
   * @param warehouse 更新する倉庫情報
   */
  const updateWarehouse = async (warehouse: UpdateWarehouseRequest) => {
    setIsLoading(true);
    try {
      const updatedWarehouse = await updateWarehouseApi({ ...warehouse, operation: 1 });
      // console.log("送信する更新倉庫情報:", { ...warehouse, operation: 1 });
      setWarehouseList((prevList) =>
        prevList.map((w) => (w.depot_cd === updatedWarehouse.depot_cd ? updatedWarehouse : w)),
      );
      // console.log("更新された倉庫情報:", updatedWarehouse); // 更新された倉庫情報をコンソールに出力
    } catch (err) {
      console.error(err);
      showAlert("倉庫の更新に失敗しました。", "error");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 倉庫情報を削除する関数
   * @param depot_kind 削除する倉庫の種別
   * @param depot_cd 削除する倉庫のコード
   */
  const deleteWarehouse = async (depot_kind:number,depot_cd: string) => {
    setIsLoading(true);
    try {
      await updateWarehouseApi({ depot_kind, operation: 9, depot_cd });
      setWarehouseList((prevList) => prevList.filter((w) => w.depot_cd !== depot_cd));
      setTotal(prev => prev - 1);
      // console.log("削除された倉庫コード:", depot_cd); // 削除された倉庫コードをコンソールに出力
    } catch (err) {
      console.error(err);
      showAlert("倉庫の削除に失敗しました。", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return [
    {
      total,
      warehouseList,
      isLoading,
    },
    {
      fetchWarehouses, // 取得関数
      registerWarehouse, // 登録関数
      updateWarehouse, // 更新関数
      deleteWarehouse, // 削除関数
    },
  ] as const;
}
